import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { RequireAuth, RequirePermission } from '@/shared/auth/guards'
import { PERM, type PermissionAction } from '@/shared/auth/types'
import { PageSpinner } from '@/shared/ui/Brand'
import LoginPage from '@/features/auth/LoginPage'
import LandingPage from '@/features/portal/LandingPage'
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from '@/features/portal/ErrorPages'
import { BILLING_MODULE, PIUTANG_MODULE } from './modules'

// The landing page ships in the entry chunk: it is the first paint, and
// lazy-loading it would put a spinner on the most-visited screen. The whole
// billing module is split behind the shell, so a user who only opens
// Monitoring never downloads it.
const ModuleShell = lazy(() => import('./ModuleShell'))
const BillingHome = lazy(() => import('@/features/billing/BillingHome'))
const ProformaListPage = lazy(() => import('@/features/billing/proforma/ProformaListPage'))
const ProformaFormPage = lazy(() => import('@/features/billing/proforma/ProformaFormPage'))
const ApprovalInboxPage = lazy(() => import('@/features/billing/approval/ApprovalInboxPage'))
const InvoiceListPage = lazy(() => import('@/features/billing/invoice/InvoiceListPage'))
const CustomerListPage = lazy(() => import('@/features/billing/customer/CustomerListPage'))
const CustomerRedirect = lazy(() => import('@/features/billing/customer/CustomerPanel'))
const ReconciliationPage = lazy(() => import('@/features/billing/recon/ReconciliationPage'))
const ApprovalMatrixPage = lazy(() => import('@/features/billing/settings/ApprovalMatrixPage'))
const PermissionsPage = lazy(() => import('@/features/billing/settings/PermissionsPage'))
const ReceivingAccountsPage = lazy(() => import('@/features/billing/settings/ReceivingAccountsPage'))
const AuditLogPage = lazy(() => import('@/features/admin/AuditLogPage'))
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'))
const AccountPage = lazy(() => import('@/features/account/AccountPage'))
const ChangePasswordPage = lazy(() => import('@/features/account/ChangePasswordPage'))
const MonitoringPage = lazy(() => import('@/features/piutang/MonitoringPage'))
const InvoiceDetailPage = lazy(() => import('@/features/piutang/InvoiceDetailPage'))
const PiutangReportsPage = lazy(() => import('@/features/piutang/ReportsPage'))
const LetterSettingsPage = lazy(() => import('@/features/piutang/LetterSettingsPage'))
// Opened by clients from a letter's link or QR; no login, no portal shell.
const PublicLetterPage = lazy(() => import('@/features/public-letter/PublicLetterPage'))

/** Every billing screen is permission-gated at the route, not only in the menu. */
function guarded(permCode: string, element: ReactNode, action: PermissionAction = 'view') {
  return (
    <RequirePermission permCode={permCode} action={action}>
      {element}
    </RequirePermission>
  )
}

// errorElement on each top-level route: an uncaught render error must land on
// a page that tells the user what to do, never on the router's own developer
// screen. React Router bubbles to the nearest ancestor that declares one, so
// the /billing entry covers every screen inside the shell.
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <LandingPage />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
  },
  // A module is one entry like this: its PORTAL_* gate, ModuleShell with the
  // module from modules.ts, and its screens as children. Recipe for a new one
  // in docs/konvensi-ui.md.
  {
    path: BILLING_MODULE.basePath,
    element: (
      <RequireAuth>
        <RequirePermission permCode={BILLING_MODULE.permCode}>
          <Suspense fallback={<PageSpinner />}>
            <ModuleShell module={BILLING_MODULE} />
          </Suspense>
        </RequirePermission>
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <BillingHome /> },
      { path: 'proforma', element: guarded(PERM.BILLING_PROFORMA, <ProformaListPage />) },
      { path: 'proforma/new', element: guarded(PERM.BILLING_PROFORMA, <ProformaFormPage />, 'add') },
      { path: 'proforma/:id', element: guarded(PERM.BILLING_PROFORMA, <ProformaFormPage />) },
      { path: 'approvals', element: guarded(PERM.BILLING_APPROVAL, <ApprovalInboxPage />) },
      { path: 'invoices', element: guarded(PERM.BILLING_INVOICE, <InvoiceListPage />) },
      { path: 'reconciliation', element: guarded(PERM.BILLING_RECON, <ReconciliationPage />) },
      { path: 'customers', element: guarded(PERM.BILLING_CUSTOMER, <CustomerListPage />) },
      // A customer opens in a panel over the list now; this keeps older links working.
      { path: 'customers/:id', element: guarded(PERM.BILLING_CUSTOMER, <CustomerRedirect />) },
      {
        path: 'settings/approval-matrix',
        element: guarded(PERM.BILLING_MATRIX, <ApprovalMatrixPage />, 'edit'),
      },
      {
        path: 'settings/receiving-accounts',
        element: guarded(PERM.BILLING_BANK_ACCOUNT, <ReceivingAccountsPage />),
      },
      {
        path: 'settings/permissions',
        element: guarded(PERM.BILLING_ADMIN, <PermissionsPage />, 'edit'),
      },
      {
        path: 'settings/audit',
        element: guarded(PERM.BILLING_ADMIN, <AuditLogPage />),
      },
      // No permission gate: everybody has notifications, and putting their
      // own behind an administrator's checkbox is how somebody ends up never
      // learning it was their turn to approve.
      { path: 'notifications', element: <NotificationsPage /> },
      // Account screens sit inside the shell rather than at the top level:
      // they are reached from the menu, and a user who lands there still needs
      // the navigation to get back out.
      { path: 'account', element: <AccountPage /> },
      { path: 'account/change-password', element: <ChangePasswordPage /> },
      // Keeps a mistyped billing URL inside the shell, so the menu is still
      // there to get out with.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: PIUTANG_MODULE.basePath,
    element: (
      <RequireAuth>
        <RequirePermission permCode={PIUTANG_MODULE.permCode}>
          <Suspense fallback={<PageSpinner />}>
            <ModuleShell module={PIUTANG_MODULE} />
          </Suspense>
        </RequirePermission>
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: guarded(PERM.PIUTANG_INVOICE, <MonitoringPage />) },
      { path: 'invoices/:id', element: guarded(PERM.PIUTANG_INVOICE, <InvoiceDetailPage />) },
      { path: 'reports', element: guarded(PERM.PIUTANG_INVOICE, <PiutangReportsPage />) },
      { path: 'settings', element: guarded(PERM.PIUTANG_SETTINGS, <LetterSettingsPage />) },
      // The same personal pages as in Billing, inside this shell, so the menu stays put.
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'account/change-password', element: <ChangePasswordPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    // The token in the address is the authorisation: deliberately outside RequireAuth.
    path: '/d/:token',
    element: (
      <Suspense fallback={<PageSpinner />}>
        <PublicLetterPage />
      </Suspense>
    ),
    errorElement: <RouteErrorPage />,
  },
  { path: '/403', element: <ForbiddenPage /> },
  { path: '*', element: <NotFoundPage /> },
])
